import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import SettingsSection from './components/SettingsSection';
import NotificationSettings from '../../components/NotificationSettings';
import ActiveSessions from '../../components/ActiveSessions';
import DifficultySettings from './components/DifficultySettings';
import IndustrySettings from './components/IndustrySettings';
import AccountSettings from './components/AccountSettings';
import PrivacySettings from './components/PrivacySettings';
import ThemeSettings from './components/ThemeSettings';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';

const Settings = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { tenantSlug } = useTenant();
  const [activeTab, setActiveTab] = useState('notifications');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const userStreak = profile?.current_streak || 0;
  const currentIndustry = profile?.industry || '';
  const userProfile = {
    name: profile?.full_name || '',
    email: user?.email || ''
  };

  // Hide industry tab for white-label tenants (industry is fixed by tenant)
  const isWhiteLabelTenant = tenantSlug && tenantSlug !== 'default';
  
  const allTabs = [
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'sessions', label: 'Active Sessions', icon: 'Monitor' },
    { id: 'difficulty', label: 'Difficulty', icon: 'Target' },
    { id: 'industry', label: 'Industry', icon: 'Briefcase', hideForTenant: true },
    { id: 'appearance', label: 'Appearance', icon: 'Palette' },
    { id: 'account', label: 'Account', icon: 'User' },
    { id: 'privacy', label: 'Privacy', icon: 'Shield' }
  ];
  
  const tabs = isWhiteLabelTenant 
    ? allTabs.filter(tab => !tab.hideForTenant)
    : allTabs;

  const handleSaveSettings = async (data) => {
    try {
      let message = 'Settings saved successfully!';
      let updates = {};
      
      if (data?.type === 'profile') {
        updates = {
          full_name: data?.data?.fullName,
          email: data?.data?.email
        };
        message = 'Profile updated successfully!';
      } else if (data?.type === 'reset-progress') {
        message = 'Progress reset successfully!';
        setTimeout(() => navigate('/app/dashboard'), 2000);
      } else if (data?.type === 'delete-account') {
        message = 'Account deletion initiated. Redirecting...';
        setTimeout(() => navigate('/authentication'), 2000);
      } else if (data?.industry) {
        updates = { industry: data?.industry };
        message = `Industry updated to ${data?.industry}!`;
      } else if (data?.difficulty) {
        updates = { skill_level: data?.difficulty };
        message = `Difficulty set to ${data?.difficulty}!`;
      }
      
      // Save to database if there are updates
      if (Object.keys(updates).length > 0) {
        const { supabase } = await import('../../lib/supabase');
        const { data: { user } } = await supabase.auth.getUser();
        
        const { error } = await supabase
          .from('user_profiles')
          .update(updates)
          .eq('id', user?.id);
        
        if (error) throw error;
      }
      
      setSuccessMessage(message);
      setShowSuccessMessage(true);
      
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSuccessMessage('Error saving settings. Please try again.');
      setShowSuccessMessage(true);
      
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar userStreak={userStreak} />
      <main className="main-content max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Settings</h1>
          <p className="text-muted-foreground">Customize your coaching experience and manage your account</p>
        </div>

        {showSuccessMessage && (
          <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-center gap-3 animate-slide-in">
            <Icon name="CheckCircle" size={20} color="var(--color-success)" />
            <p className="text-sm font-medium text-foreground">{successMessage}</p>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6">
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-card rounded-xl p-2 shadow-card sticky top-24">
              <nav className="space-y-1">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                      activeTab === tab?.id
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon 
                      name={tab?.icon} 
                      size={18} 
                      color={activeTab === tab?.id ? 'currentColor' : 'var(--color-muted-foreground)'} 
                    />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <div className="flex-1">
            {activeTab === 'notifications' && (
              <SettingsSection
                title="Notification Preferences"
                description="Manage how and when you receive coaching reminders and updates"
              >
                <NotificationSettings />
              </SettingsSection>
            )}

            {activeTab === 'sessions' && (
              <SettingsSection
                title="Active Sessions"
                description="Manage devices and locations where you're signed in"
              >
                <ActiveSessions />
              </SettingsSection>
            )}

            {activeTab === 'difficulty' && (
              <SettingsSection
                title="Difficulty Level"
                description="Adjust the complexity of your coaching scenarios and feedback"
              >
                <DifficultySettings 
                  currentDifficulty={profile?.skill_level || 'beginner'}
                  onSave={handleSaveSettings} 
                />
              </SettingsSection>
            )}

            {activeTab === 'industry' && (
              <SettingsSection
                title="Industry Settings"
                description="Update your sales industry to receive relevant coaching content"
              >
                <IndustrySettings 
                  currentIndustry={currentIndustry}
                  onSave={handleSaveSettings} 
                />
              </SettingsSection>
            )}

            {activeTab === 'appearance' && (
              <SettingsSection
                title="Appearance"
                description="Customize how PitchLab looks. Choose light, dark, or follow your device settings"
              >
                <ThemeSettings />
              </SettingsSection>
            )}

            {activeTab === 'account' && (
              <SettingsSection
                title="Account Management"
                description="Update your profile information and view coaching statistics"
              >
                <AccountSettings 
                  userProfile={userProfile}
                  onSave={handleSaveSettings} 
                />
              </SettingsSection>
            )}

            {activeTab === 'privacy' && (
              <SettingsSection
                title="Privacy & Data"
                description="Control how your data is used and manage your account"
              >
                <PrivacySettings onSave={handleSaveSettings} />
              </SettingsSection>
            )}
          </div>
        </div>

        <div className="mt-8 p-6 bg-muted/50 rounded-xl">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="HelpCircle" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground mb-2">Need Help?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                If you have questions about any settings or need assistance with your account, our support team is here to help.
              </p>
              <div className="flex flex-wrap gap-3">
                <a 
                  href="mailto:support@earnmyedge.com" 
                  className="text-sm font-medium text-primary hover:underline flex items-center gap-2"
                >
                  <Icon name="Mail" size={16} />
                  support@earnmyedge.com
                </a>
                <a 
                  href="#" 
                  className="text-sm font-medium text-primary hover:underline flex items-center gap-2"
                >
                  <Icon name="FileText" size={16} />
                  View Documentation
                </a>
              </div>
            </div>
          </div>
        </div>
      </main></div>
  );
};

export default Settings;